import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Coins, Megaphone, Lightbulb, Trophy, Gamepad2, History } from 'lucide-react';
import TierProgressionWidget from './TierProgressionWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CREDIT_TYPES = [
  { key: 'AD_CREDIT', label: 'Ad Credits', icon: Megaphone, color: 'text-pink-600' },
  { key: 'LEAD_CREDIT', label: 'Lead Credits', icon: Lightbulb, color: 'text-yellow-600' },
  { key: 'BOOST_CREDIT', label: 'Boost Credits', icon: Trophy, color: 'text-purple-600' },
  { key: 'TRAINING_CREDIT', label: 'Training Credits', icon: History, color: 'text-blue-600' },
  { key: 'GAME_CREDIT', label: 'Game Credits', icon: Gamepad2, color: 'text-emerald-600' },
];

const MemberCreditsWallet = () => {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };

  const [balances, setBalances] = useState({});
  const [ledger, setLedger] = useState([]);
  const [filterType, setFilterType] = useState(null);
  const [tier, setTier] = useState(null);

  const loadBalances = async () => {
    try {
      const res = await axios.get(`${API}/member/credits/balances`, { headers: auth });
      setBalances(res.data.balances || {});
    } catch (e) {
      toast.error('Failed to load balances');
    }
  };

  const loadLedger = async () => {
    try {
      const params = { limit: 100 };
      if (filterType) params.credit_type = filterType;
      const res = await axios.get(`${API}/member/credits/ledger`, { headers: auth, params });
      setLedger(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load ledger');
    }
  };

  const loadTier = async () => {
    try {
      const res = await axios.get(`${API}/member/tier/me`, { headers: auth });
      setTier(res.data);
    } catch (e) {/* silent */}
  };

  useEffect(() => {
    loadBalances();
    loadLedger();
    loadTier();
  }, []);

  useEffect(() => {
    loadLedger();
  }, [filterType]);

  return (
    <div className="space-y-6" data-testid="member-credits-wallet">
      <TierProgressionWidget compact />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-500" />
            Credits Wallet
            {tier && (
              <Badge className="ml-2" variant="outline" data-testid="my-tier-badge">
                {tier.tier}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            One unified ledger across all 5 credit types. Tier upgrades grant a one-time starter bundle.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {CREDIT_TYPES.map(({ key, label, icon: Icon, color }) => (
              <button
                key={key}
                onClick={() => setFilterType(filterType === key ? null : key)}
                className={`text-left border rounded-lg p-4 hover:bg-muted transition-colors ${filterType === key ? 'ring-2 ring-amber-400' : ''}`}
                data-testid={`balance-card-${key}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`h-4 w-4 ${color}`} />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
                </div>
                <div className="text-2xl font-semibold" data-testid={`balance-value-${key}`}>
                  {Math.round((balances[key] ?? 0) * 100) / 100}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Ledger History {filterType && <Badge variant="outline" className="ml-2">{filterType}</Badge>}
          </CardTitle>
          <CardDescription>Append-only entries. Click a balance above to filter.</CardDescription>
        </CardHeader>
        <CardContent>
          {ledger.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">No entries yet.</div>
          ) : (
            <div className="space-y-2" data-testid="ledger-list">
              {ledger.map((e) => (
                <div key={e.id} className="border rounded p-3 flex items-center justify-between text-sm" data-testid={`ledger-entry-${e.id}`}>
                  <div>
                    <div className="font-medium">
                      <Badge variant={e.direction === 'CREDIT' ? 'default' : 'secondary'}>
                        {e.direction}
                      </Badge>
                      <span className="ml-2">{e.amount} {e.credit_type}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {e.reason}{e.note ? ` • ${e.note}` : ''}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(e.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MemberCreditsWallet;
